<template>
    <transition
        enter-class="opacity-0"
        enter-active-class="transition-opacity duration-200"
        leave-to-class="opacity-0"
        leave-active-class="transition-opacity duration-200"
    >
        <Layout v-show="ready"
                container-class="flex flex-col justify-center min-h-screen py-10 sm:min-h-0 bg-primary">
            <div v-if="ready" class="w-full max-w-sm px-10 mx-auto">
                <BackButton location="Flow" />
                <div class="flex justify-center mb-8">
                    <img :src="config.service_logo" :alt="config.service_name"
                         class="block w-auto h-12">
                </div>
                <h1 class="mb-8 text-xl font-bold leading-tight text-center text-highlight-100">
                    {{ _('mobile_number_title') }}
                </h1>
                <p class="mb-8 text-center text-highlight-60">
                    {{ _('mobile_number_description_safaricom') }}
                </p>
                <form @submit.prevent="submit">
                    <TelephoneField
                        class="mb-6"
                        :label="_('mobile_number_input')"
                        :country-code="this.params.cc"
                        v-model="phoneNumber"
                        :validation.sync="valid.phoneNumber"
                    />

                    <p v-if="displayError" class="px-4 mb-2 text-xs text-center text-error">
                        {{ _('purchase_error') }}
                    </p>

                    <BaseRadioGroup
                        class="mb-5"
                        name="payment-method"
                        :label="_('heading_payment_method_select')"
                        :options="paymentMethods"
                        v-model="paymentMethod"
                        :validation.sync="valid.paymentMethod"
                    />

                    <p class="px-4 mb-8 text-xs text-center text-highlight-60">
                        {{ _('mobile_number_terms_safaricom') }}
                    </p>

                    <BaseButton type="button" :disabled="!isValid">
                        {{ _('mobile_number_button') }}
                    </BaseButton>
                </form>
            </div>
            <Portal to="spinner">
                <div v-if="loading"
                     class="fixed inset-0 flex flex-col items-center justify-center p-8 bg-background-80">
                    <div class="mb-20">
                        <BaseSpinner/>
                    </div>
                </div>
            </Portal>
        </Layout>
    </transition>
</template>

<script>
import { mapGetters } from 'vuex'
import NProgress from 'nprogress'
import Layout from '@/components/Layout.vue'
import BaseButton from '@/components/Base/BaseButton.vue'
import BaseSpinner from '@/components/Base/BaseSpinner.vue'
import TelephoneField from '@/components/Form/Fields/TelephoneField.vue'
import BaseRadioGroup from '@/components/Base/BaseRadioGroup.vue'
import BackButton from '@/components/BackButton.vue'
import store from '../../store'

export default {
    components: {
        BackButton,
        BaseRadioGroup,
        Layout,
        BaseButton,
        BaseSpinner,
        TelephoneField,
    },
    data() {
        return {
            paymentMethods: {
                airtime: 'Airtime',
                mpesa: 'Mpesa',
            },
            phoneNumber: '',
            paymentMethod: null,
            displayError: false,
            valid: {
                phoneNumber: false,
                paymentMethod: false,
            },
            ready: false,
            loading: false,
            config: '',
        }
    },
    computed: {
        ...mapGetters({
            fetched: 'localisation/fetched',
            _: 'localisation/translate',
            client: 'settings/getClient',
            pack: 'packs/pack',
            selected: 'packs/selected',
            params: 'settings/get',
            authenticated: 'user/authenticated',
            user: 'user/get',
            id: 'charge/id',
            error: 'charge/error',
            provider: 'providers/selected',
            chargeStatus: 'charge/status',
            redeemed: 'packs/redeemed',
        }),
        isValid() {
            return Object.keys(this.valid)
                .every((field) => this.valid[field])
        },
        title() {
            return this.$router.currentRoute.name
        },
        accessToken() {
            return Object.keys(store.getters['user/authenticated']).length
                ? store.getters['user/authenticated'].access_token
                : this.$route.query.access_token
        },
        plan() {
            return this.selected !== null
                ? store.getters['packs/findPlan'](store.getters['packs/selected'])
                : null
        },
    },
    watch: {
        fetched: {
            immediate: true,
            deep: true,
            handler(fetched) {
                if (fetched) {
                    setTimeout(() => {
                        this.ready = true
                        NProgress.done()
                    }, 1000)
                }
            },
        },
        error: {
            immediate: true,
            handler(error) {
                if (error) {
                    this.loading = false
                    this.displayError = true
                }
            },
        },
        chargeStatus: {
            immediate: true,
            handler(chargeStatus) {
                if (chargeStatus === 'ACCEPTED') {
                    this.dispatchCallback()
                    this.$router.push('/confirmation')
                }
            },
        },
    },
    created() {
        NProgress.start()
    },
    async mounted() {
        this.config = await import(`@/resources/${this.client}`)
        this.$store.dispatch('charge/resetErrors')

        if (this.plan === null && this.redeemed === false) {
            this.$router.push('/')
        }

        this.phoneNumber = this.user.phone_number || '';
    },
    methods: {
        async submit() {
            if (this.redeemed === false) {
                this.chargeUser()
            } else {
                this.validateRedeem()
            }
        },
        async validateRedeem() {
            this.loading = true
            console.log('Trigger validation')
        },
        async chargeUser() {
            if (this.isValid) {
                this.loading = true
                this.$store.dispatch('charge/resetErrors')

                try {
                    await this.$store.dispatch('charge/createChargeWithPhoneNumber', {
                        vendor: this.provider.name.toLowerCase(),
                        userId: this.user.uuid,
                        msisdn: this.phoneNumber,
                        plan: this.plan,
                        access_token: this.accessToken,
                        uvid: this.params.uvid,
                        paymentMethod: this.paymentMethod,
                    })
                        .then((res) => {
                            if (res.data.code === 1000) {
                                this.$store.dispatch('charge/checkStatus', {
                                    vendor: this.provider.name.toLowerCase(),
                                    msisdn: this.phoneNumber.replace('+', ''),
                                    transactionId: this.plan.external_id,
                                    access_token: this.accessToken,
                                })
                            }
                        })
                } catch (e) {
                    this.loading = false
                    this.$store.dispatch('charge/purchaseFailed', e.response.data.error)
                }
            }
        },
        dispatchCallback() {
            if (typeof AndroidCallbacks !== 'undefined') {
                // eslint-disable-next-line no-undef
                AndroidCallbacks.userSubscribedToTelco('Safaricom')
            }

            try {
                // eslint-disable-next-line no-undef
                webkit.messageHandlers.userSubscribedToTelco.postMessage('Safaricom')
            } catch (error) {
                // Not an iOS device
            }
        },
    },
}
</script>
